import {FC, useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {Box} from '@mui/material';
//store
import {clearAuctionData, getAuctionList, getAuctionListSelector} from '@/store/organization-auction';
import {getCurrentEventSelector, getEventsSelector} from '@/store/organization-event';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
//components
import EmptyContent from '@/shared/components/Table/EmptyContent';
import Modal from '@/shared/components/Modal';
import Loader from '@/shared/components/Loader';
import AuctionTable from '@/components/Organization/Auction/AuctionTable';
//helpers
import {getDefaultRequestBody, getErrorMessage} from '@/utils/helpers';
//constants
import {ADD_ITEM, CREATE_ITEM, EVENT_ID} from '@/utils/constants';
//icons
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';
//styles
import {auctionStyle} from './Auction.styles';
import {Formik, FormikProps} from 'formik';
import {
  AuctionFormValues,
  initialValues,
  validationSchema,
} from '@/components/Organization/Auction/AuctionTable/AuctionItem/formConfiguration';
import FormContent from '@/components/Organization/Auction/AuctionTable/AuctionItem/FormContent';
import {OrganizationAuctionService} from '@/utils/services';
import {sendPresignedImage} from '../Profile/General/ChangePhotoModal';
import {sendPresignedImages} from '@/components/Organization/Auction/AuctionTable/AuctionItem';
import {getDashboardStatisticsSelector} from '@/store/organization-dashboard';

const Auction: FC = () => {
  const dispatch = useAppDispatch();

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [isAddAuctionModalOpen, setIsAddAuctionModalOpen] = useState(false);

  const events = useAppSelector(getEventsSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const auctionList = useAppSelector(getAuctionListSelector);
  const statistics = useAppSelector(getDashboardStatisticsSelector);

  const formikRef = useRef<FormikProps<AuctionFormValues>>(null);

  useEffect(() => {
    if (currentEvent) getAuctionListData();
    else if (events && !events?.length) setIsDataLoaded(true);
  }, [currentEvent, events?.length]);

  useEffect(() => {
    return () => {
      dispatch(clearAuctionData());
    };
  }, [dispatch, isAddAuctionModalOpen]);

  const getAuctionListData = async () => {
    setIsDataLoaded(false);

    try {
      const reqBody = getDefaultRequestBody(currentEvent!.id, EVENT_ID);
      await dispatch(getAuctionList(reqBody)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  const handleCloseAuctionModal = () => {
    formikRef.current?.resetForm();
    setIsAddAuctionModalOpen(false);
  };

  const handleCreateItem = async (values: AuctionFormValues) => {
    if (!currentEvent) return;

    try {
      const presignedImgUrl = await OrganizationAuctionService.getPresingUrl();

      try {
        await sendPresignedImages(presignedImgUrl, values.images);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }

      const images = presignedImgUrl.data.slice(0, values.images.length).map((data, index) => {
        const segments = data.key.split('/');
        const image = segments[segments.length - 1];
        return {image, isMain: index === 0};
      });

      const reqBody = {...values, eventId: currentEvent.id, images, donors: []};

      const {data} = await OrganizationAuctionService.createAuction(reqBody);

      if (data) {
        await Promise.all(
          values.donors.map(async (donor) => {
            const presignedDonorsUrl = await OrganizationAuctionService.getPresingUrlForDonor();

            await sendPresignedImage(presignedDonorsUrl.data.url, donor.logo);

            const donorImageData = {
              name: donor.name,
              logo: presignedDonorsUrl.data.key.split('/').pop()!,
            };

            await OrganizationAuctionService.createDonor(data, donorImageData);
          })
        );
      }

      getAuctionListData();

      handleCloseAuctionModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Box sx={auctionStyle.wrapper}>
      {isDataLoaded ? (
        <>
          {!!auctionList?.data?.length || statistics?.allItems ? (
            <AuctionTable />
          ) : (
            <EmptyContent
              title="You do not have any items."
              icon={JudgeIcon}
              buttonTitle={CREATE_ITEM}
              onModalChange={() => setIsAddAuctionModalOpen(true)}
            />
          )}
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleCreateItem}>
            <Modal
              title={ADD_ITEM}
              onClose={handleCloseAuctionModal}
              open={isAddAuctionModalOpen}
              sx={auctionStyle.modal}
            >
              <FormContent onCloseModal={handleCloseAuctionModal} />
            </Modal>
          </Formik>
        </>
      ) : (
        <Loader parentClassName={auctionStyle.loader} size={50} />
      )}
    </Box>
  );
};

export default Auction;
